import { APP_BASE_HREF } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { ApplicationConfig, inject } from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';
import { environment } from '@environments/environment';
import { errorInterceptor } from '@interceptors/error';
import { tokenInterceptor } from '@interceptors/token';
import { ResellerAppPersistentStateService } from '@services/app-persistent-state.service';
import { ResellerProvidersService } from '@services/providers.service';
import { ResellerPurchaseHistoryService } from '@services/purchase-history.service';
import { ResellerToastService } from '@services/toast.service';
import { LicenseTypesStore } from '@store/license-types.store';
import { ResellerPurchaseHistoryWrapper } from '@wrappers/purchase-history.wrapper';
import { I18N_PROVIDERS, I18_NAMESPACES_MBS_RESELLER, I18_NAMESPACES_MBS_UI_KIT, I18_NAMESPACES_MBS_UI_SHARED } from 'i18n';
import { ModalService, SHARED_PERSISTENT_STATE_PROVIDERS, ToastService } from 'mbs-ui-kit';
import { MBS_APP_CONFIG } from 'mbs-ui-shared';
import { BALANCE_TOKEN } from 'mbs-ui-shared@components/licenses/tokens/balance.token';
import { PurchaseHistoryService } from 'mbs-ui-shared@components/purchase-history/services/purchase-history.service';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LicensesSignalFacade } from 'mbs-ui-shared@facades/licenses-signal.facade';
import { redirectInterceptor } from 'mbs-ui-shared@interceptors/redirect.interceptor';
import { AppPersistentStateService } from 'mbs-ui-shared@services/app-persistent-state.service';
import { POST_PAYMENT_TOKEN } from 'mbs-ui-shared@tokens/post-payment.token';
import { PROVIDERS_TOKEN } from 'mbs-ui-shared@tokens/providers.token';
import { SHARED_LIB_PROJECT_CONFIG, SharedLibProject } from 'mbs-ui-shared@tokens/shared-lib-project.token';
import { of } from 'rxjs';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    // provideZonelessChangeDetection(),
    // v22 flips the router default to 'always'; pinned to the pre-22 behavior
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })),
    provideHttpClient(withXhr(), withInterceptors([tokenInterceptor, errorInterceptor, redirectInterceptor])),
    { provide: APP_BASE_HREF, useValue: '/' },
    ...I18N_PROVIDERS(
      environment.localePath,
      [...I18_NAMESPACES_MBS_RESELLER, ...I18_NAMESPACES_MBS_UI_SHARED, ...I18_NAMESPACES_MBS_UI_KIT],
      'mbs-reseller'
    ),
    ...SHARED_PERSISTENT_STATE_PROVIDERS,
    { provide: SHARED_LIB_PROJECT_CONFIG, useValue: SharedLibProject.MbsReseller },
    { provide: POST_PAYMENT_TOKEN, useValue: of(false) },
    { provide: BALANCE_TOKEN, useValue: of(null) },
    { provide: PROVIDERS_TOKEN, useFactory: () => inject(ResellerProvidersService).getTypes() },
    { provide: PurchaseHistoryService, useClass: ResellerPurchaseHistoryWrapper },
    { provide: AppPersistentStateService, useClass: ResellerAppPersistentStateService },
    { provide: ResellerAppPersistentStateService, useExisting: AppPersistentStateService },
    { provide: ResellerPurchaseHistoryService, useClass: ResellerPurchaseHistoryService },
    { provide: ToastService, useClass: ResellerToastService },
    { provide: ResellerToastService, useExisting: ToastService },
    ModalService,
    {
      provide: AuthFacade,
      useValue: { currentUser$: of({ ProviderInfo: { TimeZoneOffset: 0 } }) } as unknown as AuthFacade
    },
    {
      provide: LicensesSignalFacade,
      useFactory: () => {
        const store = inject(LicenseTypesStore);

        return {
          allLicenseTypesMap: store.allLicenseTypesMap,
          allLicenseTypesLoaded: store.allLicenseTypesLoaded,
          loadAllLicenseTypes: (force = false) => store.loadAllLicenseTypes(force)
        } as unknown as LicensesSignalFacade;
      }
    },
    { provide: MBS_APP_CONFIG, useValue: {} }
  ]
};
